import React, { useEffect, useState } from "react";
import sendServerLogging from "../../utils/sendServerLogging";
import userJSON from "../../utils/userJSON";

export default function FeedbackModal({
  patient,
  rating,
  setRating,
  setUserFeedback,
  setShowFeedbackModal,
  userFeedback, 
}) {
  const [hoveredRating, setHoveredRating] = useState(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [showAdditionalFeedback, setShowAdditionalFeedback] = useState(false);

  // Initialize state from existing values when modal opens
  useEffect(() => {
    if (rating > 0) {
      setShowAdditionalFeedback(true);
    }

    // If userFeedback exists, populate the additional feedback
    if (userFeedback?.additional_feedback) {
      setAdditionalFeedback(userFeedback.additional_feedback);
    }
  }, [rating, userFeedback]);

  // Create the feedback object
  const feedbackObject = {
    rating: rating,
    additional_feedback: additionalFeedback,
    timestamp: Math.floor(Date.now() / 1000),
  };

  const handleSubmit = () => {
    //save feedback in server logging table
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "FEEDBACK_SURVEY_SUBMITTED",
        eventData: {
          mrn: patient.mrn,
          additional_feedback: additionalFeedback,
          rating: rating,
        },
        patientId: patient.patientId,
      },
    });

    setUserFeedback(feedbackObject);
    setShowFeedbackModal(false);
  };

  const handleRatingClick = (star) => {
    setRating(star);
    // handles showing the additional feedback text box
    setShowAdditionalFeedback(true);
  };

  return (
    <div className="fixed bg-gray-800 bg-opacity-80 inset-0 flex items-center justify-center z-50">
      <div className="relative flex flex-col items-center bg-white p-8 rounded-lg shadow-lg w-[80vw] max-w-[600px]">
        <button
          onClick={() => setShowFeedbackModal(false)}
          className="text-lg absolute top-3 right-5 font-extrabold text-accent1 hover:opacity-70"
        >
          X
        </button>

        <h1 className="text-2xl text-accent1 font-bold mt-4 mb-2">
          Rate Your Experience
        </h1>
        <p className="text-accent1 mb-6">
          How is your experience with Knowtex so far?
        </p>

        {/* Star Rating */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => handleRatingClick(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="transition-all duration-200"
            >
              <svg
                className="w-10 h-10"
                fill={star <= (hoveredRating || rating) ? "#FFD700" : "#E5E7EB"}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          ))}
        </div>

        {/* Feedback Text Area */}
        {showAdditionalFeedback && (
          <textarea
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
            placeholder="Leave feedback here."
            className="w-full p-3 border border-gray-300 placeholder:italic rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-accent1"
            rows="4"
          />
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={rating === 0}
          className={`mt-6 px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
            rating === 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-accent2 hover:bg-purple2 text-white"
          }`}
        >
          Submit Feedback
        </button>
      </div>
    </div>
  );
}
